import { supabase, supabaseRpc, getCurrentUserId } from '@/lib/supabase'
import type { Database } from '@/lib/database.types'
import { shopService } from '@/services/shop.service'

type ChildRow = Database['public']['Tables']['children']['Row']
type ChildInsert = Database['public']['Tables']['children']['Insert']
type ChildUpdate = Database['public']['Tables']['children']['Update']

// Le store children, TasksPage et les drawers de formulaire demandent tous la
// même liste au même moment (HARMONIA-3 : 4 fetchs identiques par navigation).
const GET_HOUSEHOLD_CHILDREN_CACHE_TTL_MS = 4000;

export const supabaseChildrenService = {
    _householdChildrenCache: null as Map<string, { data: ChildRow[]; ts: number }> | null,
    _householdChildrenInFlight: null as Map<string, Promise<ChildRow[]>> | null,

    _invalidateHouseholdChildrenCache() {
        this._householdChildrenCache?.clear();
    },

    /**
     * Create a child
     */
    async createChild(childData: ChildInsert): Promise<ChildRow> {
        try {
            if (!childData.household_id) {
                throw new Error('household_id is required to create a child.');
            }

            // Vérifier l'authentification et la vérification email
            const { data: { user } } = await supabase.auth.getUser()
            if (!user) {
                throw new Error('User not authenticated')
            }

            // Permettre aux utilisateurs anonymes de créer des enfants sans vérification d'email
            if (!user.is_anonymous && !user.email_confirmed_at) {
                throw new Error('Veuillez vérifier votre adresse email avant d\'effectuer des modifications. Vérifiez votre boîte de réception pour un lien de vérification.')
            }

            // 1. Verify that the household exists
            const { data: household, error: householdError } = await supabase
                .from('households')
                .select('id')
                .eq('id', childData.household_id)
                .single();

            if (householdError || !household) {
                console.error('Error fetching household or household not found:', householdError);
                throw new Error('Household not found or error fetching it.');
            }

            // 2. Create the child
            const { data, error } = await supabase
                .from('children')
                .insert(childData)
                .select()
                .single();

            if (error) {
                console.error('Supabase error creating child:', error);
                throw error;
            }

            this._invalidateHouseholdChildrenCache();

            // 3. Grant free starter items (avatar, pose, background) from shop
            try {
                const starterItems = await shopService.grantFreeStarterItems(data.id);
                console.log(`✅ [supabaseChildrenService] Free starter items granted to ${data.name}:`, starterItems);
            } catch (avatarError) {
                console.error(`⚠️ [supabaseChildrenService] Failed to grant starter items to ${data.name}:`, avatarError);
                // Don't block child creation if avatar assignment fails
            }

            return data;
        } catch (error) {
            console.error('Error creating child:', error)
            throw error
        }
    },

    /**
     * Get children for a household
     */
    async getHouseholdChildren(householdId: string): Promise<ChildRow[]> {
        const now = Date.now();
        if (!this._householdChildrenCache) this._householdChildrenCache = new Map();
        const cached = this._householdChildrenCache.get(householdId);
        if (cached && now - cached.ts < GET_HOUSEHOLD_CHILDREN_CACHE_TTL_MS) {
            return cached.data;
        }

        if (!this._householdChildrenInFlight) this._householdChildrenInFlight = new Map();
        const inFlight = this._householdChildrenInFlight.get(householdId);
        if (inFlight) return inFlight;

        const request = (async (): Promise<ChildRow[]> => {
            // Pas de pré-check d'accès ici : checkHouseholdAccessSafe retourne
            // toujours true (fallback temporaire) et coûtait getUser + 2 RPC par
            // appel. RLS et le RPC restent les garde-fous.
            const { data, error } = await supabaseRpc
                .rpc('get_household_children_safe', {
                    p_household_id: householdId
                });

            if (error) {
                console.error('Error in getHouseholdChildren:', error);
                throw error;
            }

            // Add created_by field to match expected type
            return (data || []).map((child: any) => ({
                ...child,
                created_by: null // RPC doesn't return this field
            }));
        })();

        this._householdChildrenInFlight.set(householdId, request);
        try {
            const result = await request;
            this._householdChildrenCache!.set(householdId, { data: result, ts: Date.now() });
            return result;
        } finally {
            if (this._householdChildrenInFlight!.get(householdId) === request) {
                this._householdChildrenInFlight!.delete(householdId);
            }
        }
    },

    /**
     * Get child by ID (safe version using RPC)
     */
    async getChild(childId: string): Promise<ChildRow | null> {
        // Lecture : l'id sert de paramètre au RPC, qui refait le contrôle d'accès.
        const userId = await getCurrentUserId()
        if (!userId) throw new Error('User not authenticated')
        const user = { id: userId }

        // Use RPC function to get child safely and check access in one call
        const { data: child, error } = await supabaseRpc
            .rpc('get_child_safe', {
                p_child_id: childId,
                p_user_id: user.id
            })

        if (error) {
            if (error.code === 'PGRST116') return null // Not found
            throw error
        }

        // RPC function may return an array, so extract the first element if needed
        if (child && Array.isArray(child)) {
            const firstChild = child.length > 0 ? child[0] : null;
            return firstChild ? {
                ...firstChild,
                created_by: null // RPC doesn't return this field
            } : null;
        }

        // RPC function returns null if no access or child not found
        return child
    },

    /**
     * Update child
     */
    async updateChild(childId: string, updates: ChildUpdate): Promise<ChildRow> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        // Vérifier si l'utilisateur a vérifié son email (sauf pour les utilisateurs anonymes)
        if (!user.is_anonymous && !user.email_confirmed_at) {
            throw new Error('Please verify your email address before making changes. Check your inbox for a verification link.')
        }

        // console.log('🔄 [supabaseChildrenService] updateChild called', {
        //     childId,
        //         userId: user.id,
        //             updates
        // });

        // Essayer d'abord la fonction RPC update_child_safe
        try {
            // Nettoyer les données avant l'envoi
            const cleanUpdates = {
                ...updates,
                // Convertir les chaînes vides en null pour les dates
                birth_date: updates.birth_date === '' ? null : updates.birth_date
            };

            const { data: rpcData, error } = await supabaseRpc
                .rpc('update_child_safe', {
                    p_child_id: childId,
                    p_user_id: user.id,
                    p_name: cleanUpdates.name,
                    p_birth_date: cleanUpdates.birth_date,
                    p_color: cleanUpdates.color,
                    p_avatar: cleanUpdates.avatar,
                    // Sans ce paramètre le genre était perdu à chaque
                    // enregistrement : le RPC ne le recevait pas, et le repli
                    // « update direct » qui le gère ne s'exécute qu'en cas
                    // d'échec du RPC. Voir 20260726110000_update_child_safe_gender.
                    p_gender: (cleanUpdates as { gender?: string | null }).gender ?? null
                })

            if (error) {
                console.error('❌ [supabaseChildrenService] RPC error updating child:', error)
                throw error
            }

            // console.log('✅ [supabaseChildrenService] updateChild RPC success', {
            //     returnedData: rpcData,
            //         dataType: typeof rpcData
            // });

            this._invalidateHouseholdChildrenCache();

            // La fonction RPC retourne un JSONB, on doit le convertir en ChildRow
            if (rpcData && typeof rpcData === 'object' && !Array.isArray(rpcData)) {
                const data = rpcData as any; // Cast to any to access properties
                const childRow: ChildRow = {
                    id: data.id,
                    household_id: data.household_id,
                    name: data.name,
                    birth_date: data.birth_date,
                    avatar: data.avatar || '',
                    points: data.points || 0,
                    color: data.color,
                    created_at: data.created_at,
                    updated_at: data.updated_at,
                    created_by: null,
                    current_level: data.current_level || 1,
                    experience_points: data.experience_points || 0,
                    total_experience: data.total_experience || 0,
                    manual_points_adjustment: data.manual_points_adjustment || 0,
                    gender: data.gender ?? null,
                    auth_user_id: data.auth_user_id ?? null,
                    coins: data.coins ?? 0,
                    has_mobile_access: data.has_mobile_access ?? null,
                    mobile_access_enabled_at: data.mobile_access_enabled_at ?? null,
                    mobile_access_enabled_by: data.mobile_access_enabled_by ?? null,
                    user_id: data.user_id ?? null,
                } as ChildRow;

                // console.log('✅ [supabaseChildrenService] Converted to ChildRow:', childRow);
                return childRow;
            }

            console.error('❌ [supabaseChildrenService] Invalid data returned from RPC:', rpcData);
            throw new Error('Invalid data returned from update_child_safe RPC');
        } catch (rpcError) {
            console.warn('⚠️ [supabaseChildrenService] RPC failed, trying direct update:', rpcError);

            // Fallback: mise à jour directe avec vérification d'accès
            const { data: child, error: getError } = await supabase
                .from('children')
                .select('household_id')
                .eq('id', childId)
                .single();

            if (getError || !child) {
                throw new Error('Child not found');
            }

            // Vérifier l'accès au household
            const { data: access, error: accessError } = await supabase
                .from('household_members')
                .select('1')
                .eq('household_id', child.household_id)
                .eq('user_id', user.id)
                .single();

            if (accessError || !access) {
                throw new Error('You do not have access to this household');
            }

            // Mise à jour directe avec données nettoyées
            const cleanUpdates = {
                ...updates,
                // Convertir les chaînes vides en null pour les dates
                birth_date: updates.birth_date === '' ? null : updates.birth_date
            };

            const { data: updatedChild, error: updateError } = await supabase
                .from('children')
                .update(cleanUpdates)
                .eq('id', childId)
                .select()
                .single();

            if (updateError) {
                console.error('❌ [supabaseChildrenService] Direct update error:', updateError);
                throw updateError;
            }

            this._invalidateHouseholdChildrenCache();

            // console.log('✅ [supabaseChildrenService] Direct update success:', updatedChild);
            return updatedChild;
        }
    },

    /**
     * Delete child
     */
    async deleteChild(childId: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        // Get child to verify access
        const child = await this.getChild(childId)
        if (!child) throw new Error('Child not found')

        // Verify user is household owner
        const { data: household } = await supabase
            .from('households')
            .select('owner_id')
            .eq('id', child.household_id)
            .single()

        if (!household || household.owner_id !== user.id) {
            throw new Error('Only household owner can delete children')
        }

        const { error } = await supabase
            .from('children')
            .delete()
            .eq('id', childId)

        if (error) throw error

        this._invalidateHouseholdChildrenCache();
    },

    /**
     * Update child points (usually handled by triggers, but can be called manually)
     */
    async updateChildPoints(childId: string, points: number): Promise<ChildRow> {
        return this.updateChild(childId, { points })
    },

    /**
     * Upload child avatar
     */
    async uploadAvatar(childId: string, file: File): Promise<string> {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        // Verify access to child
        const child = await this.getChild(childId)
        if (!child) throw new Error('Child not found')

        // Create file path
        const fileExt = file.name.split('.').pop()
        const fileName = `${childId}-${Date.now()}.${fileExt}`
        const filePath = `avatars/${fileName}`

        // Upload file to Supabase Storage
        const { error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(filePath, file, {
                cacheControl: '3600',
                upsert: false
            })

        if (uploadError) throw uploadError

        // Get public URL
        const { data: urlData } = supabase.storage
            .from('avatars')
            .getPublicUrl(filePath)

        const avatarUrl = urlData.publicUrl

        // Update child with new avatar URL
        await this.updateChild(childId, { avatar: avatarUrl })

        return avatarUrl
    },

    /**
     * Get child statistics (points, completed tasks, etc.) using daily_completions
     */
    async getChildStatistics(childId: string, dateRange?: { start: string; end: string }) {
        // Lecture protégée par RLS ; l'accès à l'enfant est revérifié juste après.
        if (!(await getCurrentUserId())) throw new Error('User not authenticated')

        // Verify access to child
        const child = await this.getChild(childId)
        if (!child) throw new Error('Child not found')

        // Build query for daily completions
        let dailyCompletionsQuery = supabase
            .from('daily_completions')
            .select('points_earned, date, tasks_data')
            .eq('child_id', childId)

        if (dateRange) {
            dailyCompletionsQuery = dailyCompletionsQuery
                .gte('date', dateRange.start)
                .lte('date', dateRange.end)
        }

        // Build query for collected rewards
        let collectedRewardsQuery = supabase
            .from('collected_rewards')
            .select('points_spent, collected_at')
            .eq('child_id', childId)

        if (dateRange) {
            const startDate = new Date(dateRange.start).toISOString()
            const endDate = new Date(dateRange.end).toISOString()
            collectedRewardsQuery = collectedRewardsQuery
                .gte('collected_at', startDate)
                .lte('collected_at', endDate)
        }

        const [dailyCompletionsResult, collectedRewardsResult] = await Promise.all([
            dailyCompletionsQuery,
            collectedRewardsQuery
        ])

        if (dailyCompletionsResult.error) throw dailyCompletionsResult.error
        if (collectedRewardsResult.error) throw collectedRewardsResult.error

        const dailyCompletions = dailyCompletionsResult.data || []
        const collectedRewards = collectedRewardsResult.data || []

        // Extract completed tasks from daily_completions.tasks_data
        const completedTasks = dailyCompletions.flatMap(completion => {
            const tasks = Array.isArray(completion.tasks_data) ? completion.tasks_data : []
            return tasks
                .filter((task: any) => task.status === 'completed')
                .map((task: any) => ({
                    points_earned: task.points || 0,
                    date: completion.date,
                    title: task.title,
                    type: task.type
                }))
        })

        return {
            currentPoints: child.points,
            totalPointsEarned: dailyCompletions.reduce((sum, completion) => sum + (completion.points_earned || 0), 0),
            totalPointsSpent: collectedRewards.reduce((sum, reward) => sum + reward.points_spent, 0),
            totalTasksCompleted: completedTasks.length,
            totalRewardsCollected: collectedRewards.length,
            completedTasks,
            collectedRewards
        }
    },

    /**
     * Helper: Check if user has access to household (safe version using RPC)
     */
    async checkHouseholdAccessSafe(householdId: string, userId: string): Promise<boolean> {
        try {
            // Check if user is owner using RPC to avoid ambiguity
            const { data: household, error: ownerError } = await supabaseRpc
                .rpc('get_household_by_id_safe', {
                    p_household_id: householdId
                });

            if (ownerError) {
                console.error('Error checking household ownership:', ownerError);
                // Temporairement autoriser l'accès en cas d'erreur
                return true;
            }

            // Si l'utilisateur est propriétaire, accès autorisé
            if (household && household.length > 0 && household[0].owner_id === userId) {
                return true;
            }

            // Check if user is member using RPC to avoid ambiguity
            const { data: isMember, error: memberError } = await supabaseRpc
                .rpc('check_household_membership', {
                    p_household_id: householdId,
                    p_user_id: userId
                });

            if (memberError) {
                console.error('Error checking household membership:', memberError);
                // Temporairement autoriser l'accès en cas d'erreur
                return true;
            }

            if (isMember) {
                return true;
            }

            // TEMPORAIRE: Autoriser l'accès même si l'utilisateur n'est pas membre
            // À retirer une fois le problème résolu
            return true;
        } catch (error) {
            console.error('Error in checkHouseholdAccessSafe:', error);
            // En cas d'erreur, on autorise temporairement l'accès pour éviter les blocages
            return true;
        }
    },

    /**
     * Helper: Check if user has access to household (legacy version - may have ambiguity issues)
     */
    async checkHouseholdAccess(householdId: string, userId: string): Promise<boolean> {
        // Redirect to safe version
        return this.checkHouseholdAccessSafe(householdId, userId);
    },

    /**
     * Helper: Check if user is member of household
     */
    async checkHouseholdMembership(householdId: string, userId: string): Promise<boolean> {
        try {
            const { data, error } = await supabase
                .from('household_members')
                .select('id')
                .eq('household_id', householdId)
                .eq('user_id', userId)

            if (error) {
                console.error('Error checking household membership:', error);
                return false;
            }

            // Vérifions si nous avons des résultats (pas besoin de .single())
            if (data && data.length > 0) {
                return true;
            }

            return false;
        } catch (error) {
            console.error('Error in checkHouseholdMembership:', error);
            return false;
        }
    },

    // Legacy API compatibility methods
    /**
     * @deprecated Use getHouseholdChildren instead
     */
    async getChildrenByHouseholdId(householdId: string): Promise<ChildRow[]> {
        return this.getHouseholdChildren(householdId)
    },

    /**
     * @deprecated Use getChild instead
     */
    async getChildById(childId: string): Promise<ChildRow | null> {
        return this.getChild(childId)
    },

    /**
     * @deprecated Use getHouseholdChildren instead
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async getChildrenByUserId(userId: string): Promise<ChildRow[]> {
        // This would need household lookup first - simplified for now
        throw new Error('getChildrenByUserId deprecated - use getHouseholdChildren with household ID')
    }
} 